import { CommonModule } from '@angular/common';
import { Component, ContentChild, DestroyRef, HostBinding, Input, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { Listbox, ListboxModule } from 'primeng/listbox';
import { getCustomFormComponentControlValueAccessorProvider } from '@lx/quote-tool/lite/shared/utils';
import { KeyboardModeService } from '@lx/shared/ui/keyboard-focus';

/**
 * Ad-hoc ListBox component that wraps PrimeNG Listbox and implements ControlValueAccessor.
 * The main purpose of this component is to preserve the selected value when users click on
 * selected option.
 *
 * This was the way that PrimeNG Listbox worked until version 17, when they changed the behavior
 * to deselect the option when clicking on it again. Since this behavior is not desired in our case,
 * we created this wrapper component to restore the previous behavior.
 *
 * To customize the items we have to tag the custom template as #customItemTemplate
 * To customize the group we have to tag the custom template as #customGroupTemplate
 */
@Component({
  standalone: true,
  selector: 'lx-list-box',
  templateUrl: './list-box.component.html',
  imports: [ListboxModule, FormsModule, CommonModule],
  providers: [getCustomFormComponentControlValueAccessorProvider(ListBoxComponent)],
})
export class ListBoxComponent<T, K> implements ControlValueAccessor, OnInit {
  @HostBinding('class.keyboard-navigation') protected withKeyboardNavigation = false;

  @ViewChild(Listbox) listbox: Listbox | undefined;

  @ContentChild('customGroupTemplate', { static: false }) customGroupTemplate: TemplateRef<unknown> | undefined;
  @ContentChild('customItemTemplate', { static: false }) customItemTemplate: TemplateRef<unknown> | undefined;

  @Input() options: T[] = [];
  @Input() optionLabel = '';
  @Input() optionValue = '';
  @Input() group = false;

  selectedValue: K = null;

  onModelChange: (fieldValue: K) => void = () => {
    // onModelChange triggered but not registered.
  };

  onTouched: () => void = () => {
    //  onTouched triggered but not registered.
  };

  private readonly keyboardModeService = inject(KeyboardModeService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.keyboardModeService.isKeyboardMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((keyboardModeEnabled) => {
      this.withKeyboardNavigation = keyboardModeEnabled;
    });
  }

  registerOnChange(onModelChange: (selectedValue: K) => void): void {
    this.onModelChange = onModelChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  writeValue(selectedValue: K): void {
    this.selectedValue = selectedValue;
  }

  /**
   * Handles the selection change event from the Listbox. If the selected option is null or undefined,
   * it restores the previous selected value. Otherwise, it updates the selected value and emits the change.
   */
  onSelectionChange(selectedOption: K) {
    if (selectedOption === null || selectedOption === undefined) {
      this.listbox?.updateModel(this.selectedValue);
    } else {
      this.onModelChange(selectedOption);
      this.writeValue(selectedOption);
    }
  }
}
